import i18n from '@/i18n'
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import type { AppNotification, NotificationEventType } from '@/types/notifications';
import type { RealtimePostgresChangesPayload } from '@supabase/supabase-js';

type NotificationRow = {
  id: string;
  account_id: string;
  recipient_auth_user_id: string;
  event_type: NotificationEventType;
  title: string;
  body: string;
  contact_id: string | null;
  opportunity_id: string | null;
  created_at: string;
  read_at: string | null;
};

function mapRow(row: NotificationRow): AppNotification {
  return {
    id: row.id,
    accountId: row.account_id,
    eventType: row.event_type,
    title: row.title,
    body: row.body,
    createdAt: row.created_at,
    readAt: row.read_at,
    contactId: row.contact_id,
    opportunityId: row.opportunity_id,
  };
}

export function useNotifications(limit = 50) {
  const { user } = useAuth();
  const accountId = user?.account_id ?? null;
  const [authUserId, setAuthUserId] = useState<string | null>(user?.auth_user_id ?? null);

  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function resolveAuthUserId() {
      if (user?.auth_user_id) {
        setAuthUserId(user.auth_user_id);
        return;
      }
      if (!supabase) {
        setAuthUserId(null);
        return;
      }
      const { data } = await supabase.auth.getUser();
      if (!cancelled) {
        setAuthUserId(data.user?.id ?? null);
      }
    }

    void resolveAuthUserId();
    return () => {
      cancelled = true;
    };
  }, [user?.auth_user_id]);

  const unreadCount = useMemo(
    () => notifications.filter((item) => item.readAt === null).length,
    [notifications]
  );

  const load = useCallback(async () => {
    if (!accountId || !authUserId || !supabase) {
      setNotifications([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('account_notifications')
        .select(
          'id, account_id, recipient_auth_user_id, event_type, title, body, contact_id, opportunity_id, created_at, read_at'
        )
        .eq('account_id', accountId)
        .eq('recipient_auth_user_id', authUserId)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) throw error;
      setNotifications((data || []).map((row) => mapRow(row as NotificationRow)));
    } catch (error) {
      console.error('[useNotifications] load error:', error);
      toast.error(i18n.t('notifications:loadError'));
    } finally {
      setLoading(false);
    }
  }, [accountId, authUserId, limit]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!accountId || !authUserId || !supabase) return;

    const client = supabase;
    const channel = client
      .channel(`notifications-${accountId}-${authUserId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'account_notifications',
          filter: `recipient_auth_user_id=eq.${authUserId}`,
        },
        (payload: RealtimePostgresChangesPayload<NotificationRow>) => {
          const row = payload.new as NotificationRow;
          if (!row?.id || row.account_id !== accountId) return;
          setNotifications((current) => {
            if (current.some((item) => item.id === row.id)) return current;
            return [mapRow(row), ...current].slice(0, limit);
          });
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'account_notifications',
          filter: `recipient_auth_user_id=eq.${authUserId}`,
        },
        (payload: RealtimePostgresChangesPayload<NotificationRow>) => {
          const row = payload.new as NotificationRow;
          if (!row?.id || row.account_id !== accountId) return;
          setNotifications((current) =>
            current.map((item) => (item.id === row.id ? mapRow(row) : item))
          );
        }
      )
      .subscribe((status) => {
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          console.warn('[useNotifications] realtime status:', status);
          void load();
        }
      });

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        void load();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      client.removeChannel(channel);
    };
  }, [accountId, authUserId, limit, load]);

  const markAsRead = useCallback(
    async (id: string) => {
      if (!supabase) return;

      const now = new Date().toISOString();
      setNotifications((current) =>
        current.map((item) =>
          item.id === id && item.readAt === null ? { ...item, readAt: now } : item
        )
      );

      const { error } = await supabase
        .from('account_notifications')
        .update({ read_at: now })
        .eq('id', id)
        .is('read_at', null);

      if (error) {
        console.error('[useNotifications] markAsRead error:', error);
        load();
      }
    },
    [load]
  );

  const markAllAsRead = useCallback(async () => {
    if (!accountId || !authUserId || !supabase) return;

    const now = new Date().toISOString();
    setNotifications((current) =>
      current.map((item) => (item.readAt === null ? { ...item, readAt: now } : item))
    );

    const { error } = await supabase
      .from('account_notifications')
      .update({ read_at: now })
      .eq('account_id', accountId)
      .eq('recipient_auth_user_id', authUserId)
      .is('read_at', null);

    if (error) {
      console.error('[useNotifications] markAllAsRead error:', error);
      toast.error(i18n.t('notifications:markReadError'));
      load();
    }
  }, [accountId, authUserId, load]);

  return {
    notifications,
    loading,
    unreadCount,
    markAsRead,
    markAllAsRead,
    reload: load,
  };
}
